import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Car, MessageSquare, Calendar, DollarSign, Plus, Edit, Trash2, Star, BarChart3, TrendingUp, Users, Eye, MousePointerClick, Search, X, GripVertical, Mail, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../i18n';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import AdminUsersPage from './admin/AdminUsers';
import VehiclesManagement from './admin/VehiclesManagement';
import AnalyticsStats from './admin/AnalyticsStats';
import UnifiedReviewsManagement from './admin/UnifiedReviewsManagement';
import EmailCampaigns from './admin/EmailCampaigns';
import PerformanceDashboard from './admin/PerformanceDashboard';
import UnifiedDashboard from './admin/UnifiedDashboard';
import VinConflicts from './admin/VinConflicts';
import SuspiciousPhotos from './admin/SuspiciousPhotos';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ lang, onLangChange }) => {
  const { t } = useTranslation(lang);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Sync active tab with URL path
  const currentPath = location.pathname.split('/admin/')[1] || 'dashboard';
  if (currentPath !== activeTab) {
    setActiveTab(currentPath);
  }

  const navItems = [
    { id: 'dashboard', label: t('admin.dashboard'), icon: BarChart3, path: '/admin' },
    { id: 'insights', label: (lang === 'tr' ? 'Panel & Analitik' : lang === 'es' ? 'Panel & Analítica' : 'Insights & Analytics'), icon: TrendingUp, path: '/admin/insights' },
    { id: 'vehicles', label: t('admin.vehicles'), icon: Car, path: '/admin/vehicles' },
    { id: 'users', label: t('admin.usersLabel'), icon: Users, path: '/admin/users' },
    { id: 'vin-conflicts', label: t('admin.vinConflictsTitle'), icon: AlertTriangle, path: '/admin/vin-conflicts' },
    { id: 'suspicious-photos', label: t('admin.suspiciousPhotosTab') || 'Suspicious Photos', icon: AlertTriangle, path: '/admin/suspicious-photos' },
    { id: 'email-campaigns', label: t('admin.campaignsLabel'), icon: Mail, path: '/admin/email-campaigns' },
    { id: 'all-messages', label: t('admin.messagesLabel'), icon: MessageSquare, path: '/admin/all-messages' },
    { id: 'reviews', label: t('admin.reviewsLabel'), icon: Star, path: '/admin/reviews' }
  ];

  return (
    <>
      <SEOHead 
        title="Admin Panel | AAA of Miami"
        description="Admin dashboard for AAA of Miami dealership management"
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="admin-dashboard-title">
            {t('admin.adminPanel')}
          </h1>
          {/* Admin Language Selector */}
          <div className="flex items-center gap-1 bg-white rounded-lg shadow-sm border px-1 py-1" data-testid="admin-lang-selector">
            {[
              { code: 'en', label: 'EN' },
              { code: 'es', label: 'ES' },
              { code: 'tr', label: 'TR' }
            ].map(l => (
              <button
                key={l.code}
                onClick={() => onLangChange(l.code)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  lang === l.code 
                    ? 'bg-gray-900 text-white' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                data-testid={`admin-lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="rounded-lg shadow-md mb-8" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = (item.id === 'dashboard' && activeTab === 'dashboard') || activeTab === item.id;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="flex items-center space-x-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap"
                  style={{
                    borderColor: isActive ? 'var(--accent-gold)' : 'transparent',
                    color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: isActive ? '600' : '400'
                  }}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <Routes>
          <Route index element={<DashboardStats lang={lang} />} />
          <Route path="insights" element={<UnifiedDashboard lang={lang} />} />
          {/* Backwards-compatible aliases so old bookmarks/links still land somewhere useful */}
          <Route path="performance" element={<UnifiedDashboard lang={lang} />} />
          <Route path="analytics" element={<UnifiedDashboard lang={lang} />} />
          <Route path="vehicles" element={<VehiclesManagement lang={lang} />} />
          <Route path="users" element={<AdminUsersPage lang={lang} />} />
          <Route path="vin-conflicts" element={<VinConflicts lang={lang} />} />
          <Route path="suspicious-photos" element={<SuspiciousPhotos lang={lang} />} />
          <Route path="email-campaigns" element={<EmailCampaigns lang={lang} />} />
          <Route path="all-messages" element={<UnifiedMessagesManagement lang={lang} />} />
          <Route path="reviews" element={<UnifiedReviewsManagement lang={lang} />} />
        </Routes>
      </div>
    </div>
    </>
  );
};

// Dashboard Stats Component
const DashboardStats = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (!stats) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('admin.overview')}</h2>
      
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-testid="dashboard-stats">
        <div className="rounded-lg p-6 shadow-lg border-l-4 border-blue-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <Car className="w-8 h-8 text-blue-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.total_vehicles}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.totalVehicles')}</p>
        </div>
        <div className="rounded-lg p-6 shadow-lg border-l-4 border-yellow-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-yellow-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.pending_quotes}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.pendingQuotes')}</p>
        </div>
        <div className="rounded-lg p-6 shadow-lg border-l-4 border-red-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <MessageSquare className="w-8 h-8 text-red-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.new_messages}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.newMessages')}</p>
        </div>
      </div>

      {/* Total Inventory Value - Featured Card */}
      <div className="rounded-xl p-8 shadow-2xl bg-gradient-to-br from-green-500 to-green-700 text-white" data-testid="total-inventory-value">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-10 h-10" />
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{t('admin.totalInventoryValue')}</h3>
            </div>
            <p className="text-green-100 text-sm">{t('admin.totalVehicles')}</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
              ${stats.total_inventory_value?.toLocaleString() || '0'}
            </p>
            <p className="text-green-100 text-sm mt-1">{stats.total_vehicles} {t('admin.vehicles')}</p>
          </div>
        </div>
      </div>

      {/* Auto Sync Status */}
      {stats.last_auto_sync && (
        <div className="mt-4 p-4 rounded-lg border" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Otomatik Sync Aktif
              </span>
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <span className="font-medium">Son sync:</span> {new Date(stats.last_auto_sync).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(stats.last_auto_sync).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              {stats.last_auto_sync_result && (
                <span className="ml-2">
                  ({stats.last_auto_sync_result.new_added || 0} eklendi, {stats.last_auto_sync_result.marked_sold || 0} satıldı)
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Monthly Activity Bar Chart */}
        <div className="rounded-lg p-6 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.monthlyActivity')}
          </h3>
          <Bar
            data={{
              labels: [t('common.jan'), t('common.feb'), t('common.mar'), t('common.apr'), t('common.may'), t('common.jun')],
              datasets: [
                {
                  label: t('admin.testDrivesLabel'),
                  data: [12, 19, 8, 15, 22, stats.pending_test_drives || 10],
                  backgroundColor: 'rgba(34, 197, 94, 0.7)',
                },
                {
                  label: t('admin.priceQuotesLabel'),
                  data: [8, 14, 12, 18, 16, stats.pending_quotes || 8],
                  backgroundColor: 'rgba(234, 179, 8, 0.7)',
                },
                {
                  label: t('admin.messagesLabel'),
                  data: [5, 9, 7, 11, 8, stats.new_messages || 6],
                  backgroundColor: 'rgba(239, 68, 68, 0.7)',
                }
              ]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'top', labels: { color: 'var(--text-primary)' } },
                title: { display: false }
              },
              scales: {
                y: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'var(--border-color)' } },
                x: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'var(--border-color)' } }
              }
            }}
          />
        </div>

        {/* Status Distribution Doughnut Chart */}
        <div className="rounded-lg p-6 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.requestStatusDistribution')}
          </h3>
          <Doughnut
            data={{
              labels: [t('admin.pendingTestDrives'), t('admin.pendingQuotes'), t('admin.newMessages'), t('admin.completedRequests')],
              datasets: [{
                data: [
                  stats.pending_test_drives || 0,
                  stats.pending_quotes || 0,
                  stats.new_messages || 0,
                  stats.total_vehicles || 0
                ],
                backgroundColor: [
                  'rgba(34, 197, 94, 0.7)',
                  'rgba(234, 179, 8, 0.7)',
                  'rgba(239, 68, 68, 0.7)',
                  'rgba(59, 130, 246, 0.7)'
                ],
                borderWidth: 2,
                borderColor: 'var(--border-color)'
              }]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'right', labels: { color: 'var(--text-primary)' } },
                title: { display: false }
              }
            }}
          />
        </div>

        {/* Trend Line Chart */}
        <div className="rounded-lg p-6 shadow-lg lg:col-span-2" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.trendAnalysis')}
          </h3>
          <Line
            data={{
              labels: [t('common.january'), t('common.february'), t('common.march'), t('common.april'), t('common.may'), t('common.june')],
              datasets: [
                {
                  label: t('admin.totalInquiries'),
                  data: [25, 42, 27, 44, 46, (stats.pending_test_drives + stats.pending_quotes + stats.new_messages) || 30],
                  borderColor: 'rgb(59, 130, 246)',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  tension: 0.4
                },
                {
                  label: t('admin.vehiclesSold'),
                  data: [8, 12, 10, 15, 18, 12],
                  borderColor: 'rgb(34, 197, 94)',
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  tension: 0.4
                }
              ]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'top', labels: { color: 'var(--text-primary)' } },
                title: { display: false }
              },
              scales: {
                y: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'var(--border-color)' } },
                x: { ticks: { color: 'var(--text-secondary)' }, grid: { color: 'var(--border-color)' } }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

// VehiclesManagement component moved to ./admin/VehiclesManagement.jsx

// Test Drives Management
const TestDrivesManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/test-drives`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching test drives:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/test-drives/${requestId}/status?status=${status}`);
      toast.success(t('admin.statusUpdated'));
      fetchRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('admin.statusUpdateFailed'));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('admin.testDrives')}</h2>
      
      {/* Scroll hint for mobile */}
      <div className="mb-2 text-sm text-gray-500 md:hidden">
        {t('admin.scrollToSee')}
      </div>
      
      <div className="rounded-lg shadow-md overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max" data-testid="test-drives-table">
            <thead className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.customerName')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.customerPhone')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.preferredDate')}/{t('admin.preferredTime')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap sticky right-0" style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.noRequests')}
                  </td>
                </tr>
              ) : (
                requests.map(request => (
                  <tr key={request.id}>
                    <td className="px-4 py-4">
                      <p className="font-semibold whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{request.name}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{request.email}</p>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{request.phone}</td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                      <p>{request.preferred_date}</p>
                      <p className="text-sm">{request.preferred_time}</p>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Badge variant={request.status === 'pending' ? 'secondary' : request.status === 'approved' ? 'default' : 'destructive'}>
                        {t(`admin.${request.status}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap sticky right-0" style={{ background: 'var(--bg-primary)' }}>
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={() => updateStatus(request.id, 'approved')} disabled={request.status === 'approved'}>
                          {t('admin.approve')}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => updateStatus(request.id, 'rejected')} disabled={request.status === 'rejected'}>
                          {t('admin.reject')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Quotes Management
const QuotesManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/price-quotes`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);

  const updateStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/price-quotes/${requestId}/status?status=${status}&send_email=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(status === 'approved' ? t('admin.quoteApproved') : t('admin.quoteRejected'));
      fetchRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('admin.statusUpdateFailed'));
    }
  };

  const openResponseModal = (request) => {
    setSelectedRequest(request);
    setResponseMessage('');
    setShowResponseModal(true);
  };

  const sendResponse = async () => {
    if (!responseMessage.trim()) {
      toast.error(t('admin.pleaseWriteReply'));
      return;
    }

    setSendingResponse(true);
    try {
      await axios.post(
        `${API}/price-quotes/${selectedRequest.id}/respond`,
        {
          message: responseMessage,
          send_email: true,
          send_sms: false
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success(t('admin.replySentSuccess'));
      setShowResponseModal(false);
      setResponseMessage('');
      fetchRequests();
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error(t('admin.replyFailed'));
    } finally {
      setSendingResponse(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('admin.quotes')}</h2>
      
      {/* Scroll hint for mobile */}
      <div className="mb-2 text-sm text-gray-500 md:hidden">
        {t('admin.scrollToSee')}
      </div>
      
      <div className="rounded-lg shadow-md overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max" data-testid="quotes-table">
            <thead className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.customerName')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.customerPhone')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('quote.offeredPrice')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.message')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{t('admin.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase whitespace-nowrap sticky right-0" style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.noRequests')}
                  </td>
                </tr>
              ) : (
                requests.map(request => (
                  <tr key={request.id}>
                    <td className="px-4 py-4">
                      <p className="font-semibold whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{request.name}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{request.email}</p>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{request.phone}</td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                      {request.offered_price ? `$${Number(request.offered_price).toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-4 max-w-xs truncate" style={{ color: 'var(--text-secondary)' }}>{request.message || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Badge variant={request.status === 'pending' ? 'secondary' : request.status === 'approved' ? 'default' : 'destructive'}>
                        {t(`admin.${request.status}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap sticky right-0" style={{ background: 'var(--bg-primary)' }}>
                      <div className="flex space-x-2">
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateStatus(request.id, 'approved')}
                              className="border-green-500 text-green-600 hover:bg-green-50"
                            >
                              ✓ {t('admin.approve')}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateStatus(request.id, 'rejected')}
                              className="border-red-500 text-red-600 hover:bg-red-50"
                            >
                              ✗ {t('admin.reject')}
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => openResponseModal(request)}
                          disabled={request.status === 'responded'}
                          style={{ background: 'var(--accent-gold)', color: 'white' }}
                        >
                          {request.status === 'responded' ? t('admin.replied') : t('admin.reply')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-xl font-bold mb-4">{t('admin.priceQuoteResponse')}</h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <p><strong>{t('admin.customerName')}:</strong> {selectedRequest.name} ({selectedRequest.email})</p>
              <p><strong>Telefon:</strong> {selectedRequest.phone}</p>
              <p><strong>{t('admin.offeredPrice')}:</strong> ${Number(selectedRequest.offered_price || 0).toLocaleString()}</p>
              {selectedRequest.message && (
                <p className="mt-2"><strong>Mesaj:</strong> {selectedRequest.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">{t('admin.replyMessage')}:</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="6"
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder={t('messages.writeYourReply')}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                ✉️ {t('admin.replyWillBeSent')}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowResponseModal(false)}
                disabled={sendingResponse}
              >
                {t('admin.cancel')}
              </Button>
              <Button
                onClick={sendResponse}
                disabled={sendingResponse || !responseMessage.trim()}
                style={{ background: 'var(--accent-gold)', color: 'white' }}
              >
                {sendingResponse ? t('messages.sending') : t('messages.send')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Unified Messages Management - All message types in one place
const UnifiedMessagesManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, contact, price-quote

  useEffect(() => {
    fetchAllMessages();
  }, []);

  // Update price quote status (approve/reject)
  const updatePriceQuoteStatus = async (messageId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/price-quotes/${messageId}/status?status=${status}&send_email=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(status === 'approved' ? t('admin.quoteApproved') : t('admin.quoteRejected'));
      fetchAllMessages();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('admin.operationFailed'));
    }
  };

  const fetchAllMessages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all message types in parallel (Test Drives removed)
      const [contactRes, priceQuotesRes] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/price-quotes`, { headers })
      ]);

      // Combine and tag messages with their type
      const combined = [
        ...contactRes.data.map(m => ({ ...m, messageType: 'contact', typeLabel: '💬 Contact Message' })),
        ...priceQuotesRes.data.map(m => ({ ...m, messageType: 'price-quote', typeLabel: '💰 Price Quote' }))
      ];

      // Sort by created_at (most recent first)
      combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setAllMessages(combined);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (message) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      let endpoint = '';

      if (message.messageType === 'contact') {
        endpoint = `${API}/contact/${message.id}/reply`;
      } else if (message.messageType === 'price-quote') {
        endpoint = `${API}/price-quotes/${message.id}/respond`;
      }

      console.log('Sending reply:', { endpoint, message: replyText, send_email: sendEmail });
      
      await axios.post(
        endpoint,
        { message: replyText, send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Reply sent successfully!');
      setReplyingTo(null);
      setReplyText('');
      setSendEmail(true);
      fetchAllMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
      console.error('Error details:', error.response?.data);
      
      // Extract error message
      let errorMessage = 'Failed to send reply';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
          }
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const filteredMessages = filterType === 'all' 
    ? allMessages 
    : allMessages.filter(m => m.messageType === filterType);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
          All Messages ({allMessages.length})
        </h2>

        {/* Filter buttons */}
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant={filterType === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterType('all')}
          >
            All ({allMessages.length})
          </Button>
          <Button
            size="sm"
            variant={filterType === 'contact' ? 'default' : 'outline'}
            onClick={() => setFilterType('contact')}
          >
            💬 Contact ({allMessages.filter(m => m.messageType === 'contact').length})
          </Button>
          <Button
            size="sm"
            variant={filterType === 'price-quote' ? 'default' : 'outline'}
            onClick={() => setFilterType('price-quote')}
          >
            💰 Price Quotes ({allMessages.filter(m => m.messageType === 'price-quote').length})
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredMessages.length === 0 ? (
          <div className="rounded-lg shadow-md p-8 text-center" style={{ background: 'var(--bg-primary)' }}>
            <p style={{ color: 'var(--text-secondary)' }}>No messages found</p>
          </div>
        ) : (
          filteredMessages.map(message => (
            <div key={`${message.messageType}-${message.id}`} className="rounded-lg shadow-md p-6" style={{ background: 'var(--bg-primary)' }}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{message.name}</h3>
                    <Badge variant="outline">{message.typeLabel}</Badge>
                    <Badge variant={message.status === 'responded' || message.conversation?.length > 0 ? 'default' : 'secondary'}>
                      {message.status === 'responded' || message.conversation?.length > 0 ? 'Replied' : 'Pending'}
                    </Badge>
                  </div>
                  <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                    📧 {message.email}
                  </p>
                  {message.phone && (
                    <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                      📱 {message.phone}
                    </p>
                  )}
                  {message.subject && (
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      📌 {message.subject}
                    </p>
                  )}
                  <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                    🕒 {new Date(message.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Message:</p>
                <p style={{ color: 'var(--text-secondary)' }}>{message.message || message.notes}</p>
              </div>

              {/* Conversation thread */}
              {message.conversation && message.conversation.length > 0 && (
                <div className="space-y-2 mb-4">
                  {message.conversation.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg ${
                        msg.from === 'admin'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-gray-50 border-l-4 border-gray-400'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-semibold" style={{ color: msg.from === 'admin' ? '#2563eb' : '#6b7280' }}>
                          {msg.from === 'admin' ? '👨‍💼 Admin' : '👤 Customer'}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {msg.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {replyingTo === message.id ? (
                <div className="space-y-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Send email notification</span>
                    </label>
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => sendReply(message)}>
                      {t('admin.sendReply')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                      {t('admin.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-2">
                  {message.messageType === 'price-quote' && message.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updatePriceQuoteStatus(message.id, 'approved')}
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        ✓ Onayla
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updatePriceQuoteStatus(message.id, 'rejected')}
                        className="border-red-500 text-red-600 hover:bg-red-50"
                      >
                        ✗ Reddet
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setReplyingTo(message.id)}>
                    Reply
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Messages Management
const MessagesManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    try {
      await axios.post(`${API}/contact/${messageId}/reply?reply=${encodeURIComponent(replyText)}`);
      toast.success(t('admin.replySent'));
      setReplyingTo(null);
      setReplyText('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(t('admin.replyFailed'));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('admin.messages')}</h2>
      <div className="space-y-4">
        {messages.length === 0 ? (
          <div className="rounded-lg shadow-md p-8 text-center" style={{ background: 'var(--bg-primary)' }}>
            <p style={{ color: 'var(--text-secondary)' }}>{t('admin.noRequests')}</p>
          </div>
        ) : (
          messages.map(message => (
            <div key={message.id} className="rounded-lg shadow-md p-6" style={{ background: 'var(--bg-primary)' }}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{message.name}</h3>
                    <Badge variant={message.reply ? 'default' : 'secondary'}>
                      {message.reply ? t('admin.replied') : t('admin.pending')}
                    </Badge>
                  </div>
                  <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.customerEmail')}: {message.email}
                  </p>
                  {message.phone && (
                    <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                      {t('admin.customerPhone')}: {message.phone}
                    </p>
                  )}
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.subject')}: {message.subject}
                  </p>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('admin.message')}:</p>
                <p style={{ color: 'var(--text-secondary)' }}>{message.message}</p>
              </div>

              {message.reply && (
                <div className="mb-4 p-4 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                  <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('admin.replyMessage')}:</p>
                  <p style={{ color: 'var(--text-secondary)' }}>{message.reply}</p>
                </div>
              )}

              {replyingTo === message.id ? (
                <div className="space-y-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={t('admin.typeReply')}
                    rows={4}
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => sendReply(message.id)}>
                      {t('admin.sendReply')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" onClick={() => setReplyingTo(message.id)} disabled={!!message.reply}>
                  {t('admin.reply')}
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

// UnifiedReviewsManagement component moved to ./admin/UnifiedReviewsManagement.jsx
// AnalyticsStats component moved to ./admin/AnalyticsStats.jsx

};

// Conversations Management Component
const ConversationsManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [conversations, setConversations] = useState({ price_quotes: [], test_drives: [] });
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminReply = async (requestType, requestId) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/conversations/${requestType}/${requestId}/admin-reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Reply sent successfully!');
      setReplyingTo(null);
      setReplyMessage('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  const allConversations = [
    ...conversations.price_quotes.map(q => ({ ...q, type: 'price-quote' })),
    ...conversations.test_drives.map(td => ({ ...td, type: 'test-drive' }))
  ].sort((a, b) => {
    const aLatest = a.conversation?.[a.conversation.length - 1]?.timestamp || a.created_at;
    const bLatest = b.conversation?.[b.conversation.length - 1]?.timestamp || b.created_at;
    return new Date(bLatest) - new Date(aLatest);
  });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
        Customer Conversations
      </h2>

      {allConversations.length === 0 ? (
        <div className="rounded-lg shadow-md p-8 text-center" style={{ background: 'var(--bg-primary)' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No conversations yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {allConversations.map((conv) => (
            <div key={conv.id} className="rounded-lg shadow-md p-6" style={{ background: 'var(--bg-primary)' }}>
              <div className="mb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                      {conv.name}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {conv.email} • {conv.phone}
                    </p>
                    <Badge variant="secondary" className="mt-2">
                      {conv.type === 'price-quote' ? 'Price Quote' : 'Test Drive'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Conversation Thread */}
              <div className="space-y-3 mb-4">
                {conv.conversation?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      msg.from === 'admin'
                        ? 'bg-blue-50 border-l-4 border-blue-500'
                        : 'bg-gray-50 border-l-4 border-gray-400'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-semibold" style={{ color: msg.from === 'admin' ? '#2563eb' : '#6b7280' }}>
                        {msg.from === 'admin' ? '👨‍💼 Admin' : '👤 Customer'}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(msg.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {msg.message}
                    </p>
                  </div>
                ))}
              </div>

              {/* Reply Section */}
              <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                {replyingTo === conv.id ? (
                  <div>
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      rows={3}
                      className="mb-2"
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => handleAdminReply(conv.type, conv.id)}>
                        {t('admin.sendReply')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyMessage('');
                        }}
                      >
                        {t('admin.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={() => setReplyingTo(conv.id)} size="sm">
                    Reply to Customer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
